import React, { useState, useEffect, useCallback } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  useTheme,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Badge,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import { 
  ExitToApp as ExitToAppIcon,
  NotificationsActive as NotificationsActiveIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  Inventory as InventoryIcon,
  AssignmentReturn as AssignmentReturnIcon,
  LocationOn as LocationOnIcon,
  WarningAmber as WarningAmberIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  Assessment as AssessmentIcon,
  ReportProblem as ReportProblemIcon,
  ListAlt as ListAltIcon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { useNavigate } from 'react-router-dom';

interface PlatformNotification {
  notification_id: string;
  title: string;
  message: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  is_read: boolean;
  action_url: string;
  created_at: string;
}

interface DashboardStats {
  currentAssets: number | null;
  assetsOnLoan: number | null;
  locations: number | null;
  overdueChecks: number | null;
  outOfAction: number | null;
}

const lato = 'Lato, sans-serif';

const sectionLabelSx = {
  fontFamily: lato,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'text.disabled',
  lineHeight: 1.3,
};

const sectionTitleSx = {
  fontFamily: lato,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'text.primary',
};

const dashboardSectionPaperSx = {
  p: 2.5,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.12)',
  boxShadow: 'none',
};

const dashboardItemCardSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.12)',
  borderRadius: 1.5,
  boxShadow: 'none',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    bgcolor: 'background.paper',
    borderColor: 'primary.light',
  },
};

const iconActionSx = {
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.12)',
  color: 'text.secondary',
  '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
  '&:hover': {
    bgcolor: 'action.hover',
    borderColor: 'primary.light',
    color: 'primary.main',
  },
};

type DashboardSectionProps = {
  label: string;
  title: string;
  count: number;
  icon: React.ReactNode;
  accentColor: string;
  action: React.ReactNode;
  children: React.ReactNode;
  fullWidth?: boolean;
};

const DashboardSection: React.FC<DashboardSectionProps> = ({
  label,
  title,
  count,
  icon,
  accentColor,
  action,
  children,
  fullWidth,
}) => (
  <Box
    sx={{
      ...dashboardSectionPaperSx,
      borderLeft: `3px solid ${accentColor}`,
      bgcolor: alpha(accentColor, 0.06),
      gridColumn: fullWidth ? { xs: '1 / -1' } : undefined,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Badge
          badgeContent={count}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              fontWeight: 700,
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <Box sx={{ color: accentColor, display: 'flex', '& .MuiSvgIcon-root': { fontSize: '1.25rem' } }}>
            {icon}
          </Box>
        </Badge>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={sectionLabelSx}>{label}</Typography>
          <Typography sx={sectionTitleSx} noWrap>
            {title}
          </Typography>
        </Box>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    currentAssets: null,
    assetsOnLoan: null,
    locations: null,
    overdueChecks: null,
    outOfAction: null,
  });
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [businessName, setBusinessName] = useState<string>('');
  const [platformNotifications, setPlatformNotifications] = useState<PlatformNotification[]>([]);
  const [outOfActionAssets, setOutOfActionAssets] = useState<any[]>([]);
  const [outOfActionReportDialog, setOutOfActionReportDialog] = useState<{
    open: boolean;
    asset: any | null;
    note: string | null;
    loading: boolean;
    error: string | null;
  }>({ open: false, asset: null, note: null, loading: false, error: null });
  const [pendingIncidents, setPendingIncidents] = useState<any[]>([]);
  const [dvlaHighUnreadNotifications, setDvlaHighUnreadNotifications] = useState<any[]>([]);
  const [inTransitOutgoingDeliveries, setInTransitOutgoingDeliveries] = useState<any[]>([]);
  const [isMultiBusinessManager, setIsMultiBusinessManager] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState<{
    open: boolean;
    notification: PlatformNotification | null;
  }>({
    open: false,
    notification: null,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [safetyCheckAssessment, setSafetyCheckAssessment] = useState<{
    loading: boolean;
    data: any;
    error: string | null;
    responses: any[];
  }>({
    loading: false,
    data: null,
    error: null,
    responses: [],
  });
  const [aiAnalysisDialog, setAiAnalysisDialog] = useState<{
    open: boolean;
    safetyCheckId: string | null;
    loading: boolean;
    data: any;
    error: string | null;
  }>({
    open: false,
    safetyCheckId: null,
    loading: false,
    data: null,
    error: null,
  });

  const normalizeArrayResponse = (payload: any) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.data && Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const formatMetricValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '--';
    }
    return value.toLocaleString();
  };

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoadingDashboard(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      setLoadingDashboard(false);
      setRefreshing(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [
        assetsRes,
        locationsRes,
        loanOverviewRes,
        platformNotificationsRes,
        incidentsRes
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/assets`, { headers }),
        axios.get(`${API_BASE_URL}/locations/stats`, { headers }),
        axios.get(`${API_BASE_URL}/safety-checks/stats/loan-overview`, { headers }),
        axios.get(`${API_BASE_URL}/notifications/platform`, { headers }),
        axios.get(`${API_BASE_URL}/incidents`, { headers })
      ]);

      const assetsData = normalizeArrayResponse(assetsRes.data);
      const locationsData = normalizeArrayResponse(locationsRes.data);
      const loanOverviewPayload = loanOverviewRes.data?.data ?? loanOverviewRes.data ?? {};
      const platformNotificationsData = platformNotificationsRes.data;

      const isOutOfAction = (asset: any) => {
        const v = asset.is_out_of_action;
        return v === true || v === 1 || v === '1' || v === 'true' || v === 'True';
      };
      const outOfActionList = assetsData.filter((asset: any) => isOutOfAction(asset));

      setOutOfActionAssets(outOfActionList);

      setDashboardStats({
        currentAssets: assetsData.length,
        assetsOnLoan: Number(loanOverviewPayload.loaned_out_assets_count ?? loanOverviewPayload.loanedOutAssetsCount ?? 0),
        locations: locationsData.length,
        overdueChecks: Number(loanOverviewPayload.late_returns_count ?? loanOverviewPayload.lateReturnsCount ?? 0),
        outOfAction: outOfActionList.length,
      });

      setPlatformNotifications(platformNotificationsData?.data || []);

      const incidentsData = normalizeArrayResponse(incidentsRes.data);
      const reportedOrActionRequired = incidentsData.filter(
        (i: any) => i.status === 'reported' || i.status === 'action_required'
      );
      setPendingIncidents(reportedOrActionRequired);

      let userRole: string | null = null;
      try {
        const payload = JSON.parse(atob((token || '').split('.')[1] || ''));
        userRole = payload.role || null;
      } catch {
        // ignore
      }
      const inBusinessContext = localStorage.getItem('inBusinessContext') === 'true';
      if (userRole === 'Admin' || inBusinessContext) {
        try {
          const dvlaRes = await axios.get(`${API_BASE_URL}/dvla-staff/notifications`, { headers });
          const dvlaList = Array.isArray(dvlaRes.data) ? dvlaRes.data : [];
          const highUnread = dvlaList.filter(
            (n: any) => (String(n.priority || '').toLowerCase() === 'high') && !n.read_at
          );
          setDvlaHighUnreadNotifications(highUnread);
        } catch (dvlaErr) {
          console.error('Error fetching DVLA notifications:', dvlaErr);
          setDvlaHighUnreadNotifications([]);
        }
      } else {
        setDvlaHighUnreadNotifications([]);
      }

      try {
        const outgoingRes = await axios.get(`${API_BASE_URL}/outgoing-deliveries/pending`, { headers });
        const outgoingList = outgoingRes.data?.success && Array.isArray(outgoingRes.data?.deliveries)
          ? outgoingRes.data.deliveries
          : [];
        const inTransit = outgoingList.filter(
          (d: any) => (String(d.request_status || '')).toLowerCase() === 'in_progress'
        );
        setInTransitOutgoingDeliveries(inTransit);
      } catch (outgoingErr) {
        console.error('Error fetching outgoing deliveries:', outgoingErr);
        setInTransitOutgoingDeliveries([]);
      }

      setLastUpdated(new Date().toISOString());
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);

      const isImpersonating = localStorage.getItem('impersonatingBusiness') === 'true';

      if (isImpersonating) {
        setError('Unable to load dashboard data for this business. You may not have access rights or the business data may be unavailable. You can return to the Owner Dashboard using the button above.');

        setDashboardStats({
          currentAssets: 0,
          assetsOnLoan: 0,
          locations: 0,
          overdueChecks: 0,
          outOfAction: 0,
        });
        setOutOfActionAssets([]);
        setPendingIncidents([]);
        setDvlaHighUnreadNotifications([]);
        setInTransitOutgoingDeliveries([]);
      } else {
        setError('Failed to load dashboard data. Please try again later.');
        if (err.response?.status === 401) {
          console.error('Authentication error while fetching dashboard data.');
        }
      }
    } finally {
      setLoadingDashboard(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    const impersonating = localStorage.getItem('impersonatingBusiness') === 'true';
    setIsImpersonating(impersonating);

    const inBusinessContext = localStorage.getItem('inBusinessContext') === 'true';
    setIsMultiBusinessManager(inBusinessContext);

    let businessNameFromStore = localStorage.getItem('businessName');
    if (!businessNameFromStore) {
      try {
        const businessContextStr = localStorage.getItem('businessContext');
        if (businessContextStr) {
          const businessContext = JSON.parse(businessContextStr);
          businessNameFromStore = businessContext.name;
        }
      } catch (err) {
        console.error('Error parsing businessContext:', err);
      }
    }

    setBusinessName(businessNameFromStore || '');
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch safety check assessment when notification dialog opens for safety check
  useEffect(() => {
    const extractSafetyCheckId = (actionUrl: string): string | null => {
      if (!actionUrl) return null;
      const patterns = [
        /\/safety-checks\/([^/?]+)/,
        /\/admin\/safety-checks\/analysis\/([^/?]+)/,
        /safety_check_id=([^&]+)/,
      ];
      for (const pattern of patterns) {
        const match = actionUrl.match(pattern);
        if (match) return match[1];
      }
      return null;
    };

    if (notificationDialog.open && notificationDialog.notification?.type === 'safety_check') {
      const safetyCheckId = extractSafetyCheckId(notificationDialog.notification.action_url);
      
      if (safetyCheckId) {
        setSafetyCheckAssessment({ loading: true, data: null, error: null, responses: [] });
        
        const fetchAssessment = async () => {
          try {
            const token = localStorage.getItem('token');
            
            // Fetch responses
            const responsesRes = await axios.get(
              `${API_BASE_URL}/safety-checks/${safetyCheckId}/responses`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Fetch AI analysis for safety checks
            let aiAnalysis = null;
            try {
              // For safety checks, use check-responses/analysis endpoint
              const analysisRes = await axios.get(
                `${API_BASE_URL}/check-responses/analysis/${safetyCheckId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (analysisRes.data?.success && analysisRes.data.data && analysisRes.data.data.length > 0) {
                // Get the most recent analysis
                const latestAnalysis = analysisRes.data.data[0];
                const analysisText = latestAnalysis.analysis_text || '';
                const isFail = analysisText.trim().split('\n')[0] === 'FAIL';
                
                // Format to match expected structure
                aiAnalysis = {
                  analysis_text: analysisText,
                  detailed_explanation: analysisText,
                  should_mark_out_of_action: isFail,
                  created_at: latestAnalysis.created_at,
                  analysis_id: latestAnalysis.analysis_id
                };
              }
            } catch (e) {
              // AI analysis might not exist, that's okay
              console.log('No AI analysis available:', e);
            }
            
            setSafetyCheckAssessment({
              loading: false,
              data: aiAnalysis,
              error: null,
              responses: responsesRes.data?.success ? responsesRes.data.data : [],
            });
          } catch (err: any) {
            setSafetyCheckAssessment({
              loading: false,
              data: null,
              error: err.response?.data?.message || 'Failed to load safety check assessment',
              responses: [],
            });
          }
        };
        
        fetchAssessment();
      }
    } else if (!notificationDialog.open) {
      // Reset when dialog closes
      setSafetyCheckAssessment({ loading: false, data: null, error: null, responses: [] });
    }
  }, [notificationDialog.open, notificationDialog.notification]);

  // Handle returning to the owner dashboard from impersonation mode
  const handleReturnToOwnerDashboard = () => {
    try {
      // Restore the original super admin token
      const superAdminToken = localStorage.getItem('superAdminToken');
      
      if (superAdminToken) {
        // Restore the original token and clear impersonation data
        localStorage.setItem('token', superAdminToken);
        
        // Update axios default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${superAdminToken}`;
      }
      
      // Remove impersonation flags and data
      localStorage.removeItem('impersonatingBusiness');
      localStorage.removeItem('businessContext');
      localStorage.removeItem('businessName');
      localStorage.removeItem('businessId');
      localStorage.removeItem('superAdminToken');
      
      // Even if we don't have the original token, we can still try to navigate back
      // Use a hard redirect to ensure the page is fully reloaded with correct context
      console.log('Returning to owner dashboard');
      
      // Force a hard navigation instead of using React Router's navigate
      window.location.href = '/owner/dashboard';
    } catch (error) {
      console.error('Error returning to owner dashboard:', error);
      // If any error happens during the process, clear everything and go to login
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  // Handle returning to multi-business manager dashboard
  const handleReturnToMultiBusinessDashboard = async () => {
    try {
      const multiBusinessManagerToken = localStorage.getItem('multiBusinessManagerToken');
      
      if (multiBusinessManagerToken) {
        // Restore the original token
        localStorage.setItem('token', multiBusinessManagerToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${multiBusinessManagerToken}`;
      } else {
        // If no stored token, request a new one via API
        const token = localStorage.getItem('token');
        try {
          const response = await axios.post(
            `${API_BASE_URL}/multi-business-manager/return-to-dashboard`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success && response.data.token) {
            localStorage.setItem('token', response.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
        } catch (err) {
          console.error('Error getting return token:', err);
        }
      }
      
      // Remove business context flags and data
      localStorage.removeItem('inBusinessContext');
      localStorage.removeItem('businessName');
      localStorage.removeItem('businessId');
      localStorage.removeItem('multiBusinessManagerToken');
      
      console.log('Returning to multi-business manager dashboard');
      
      // Force a hard navigation to ensure full page reload
      window.location.href = '/multi-business';
    } catch (error) {
      console.error('Error returning to multi-business dashboard:', error);
      // If any error happens, go to login
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  if (loadingDashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert>
      </Box>
    );
  }

  const metricCards = [
    {
      key: 'currentAssets',
      label: 'Current Assets',
      value: formatMetricValue(dashboardStats.currentAssets),
      helper: 'Active assets',
      color: theme.palette.primary.main,
      icon: <InventoryIcon />,
      onClick: () => navigate('/admin/assets', { 
        state: { 
          applyFilter: { 
            status: 'active' 
          } 
        } 
      }),
    },
    {
      key: 'assetsOnLoan',
      label: 'Number on Loan',
      value: formatMetricValue(dashboardStats.assetsOnLoan),
      helper: 'Assets currently loaned out',
      color: theme.palette.secondary.main,
      icon: <AssignmentReturnIcon />,
      onClick: () => navigate('/admin/assets', { 
        state: { 
          applyFilter: { 
            loanStatus: 'loaned_out' 
          } 
        } 
      }),
    },
    {
      key: 'locations',
      label: 'Locations',
      value: formatMetricValue(dashboardStats.locations),
      helper: 'Locations being monitored',
      color: theme.palette.warning.main,
      icon: <LocationOnIcon />,
      onClick: () => navigate('/admin/locations'),
    },
    {
      key: 'overdueChecks',
      label: 'Overdue',
      value: formatMetricValue(dashboardStats.overdueChecks),
      helper: 'Assets overdue for return',
      color: theme.palette.error.main,
      icon: <WarningAmberIcon />,
      onClick: () => navigate('/admin/assets', { 
        state: { 
          applyFilter: { 
            loanStatus: 'overdue' 
          } 
        } 
      }),
    },
    {
      key: 'outOfAction',
      label: 'Out of Action',
      value: formatMetricValue(dashboardStats.outOfAction),
      helper: 'Assets currently out of action',
      color: theme.palette.error.dark,
      icon: <BlockIcon />,
      onClick: () => navigate('/admin/assets', { 
        state: { 
          applyFilter: { 
            outOfAction: 'true' 
          } 
        } 
      }),
    },
  ];

  const highPriorityNotifications = platformNotifications.filter(
    (n) => !n.is_read && (n.severity === 'high' || n.severity === 'critical')
  );

  const sectionLinkButtonSx = {
    fontFamily: lato,
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    borderRadius: 1.5,
    px: 1.5,
    py: 0.5,
    minHeight: 32,
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3, px: { xs: 0, sm: 0.5 } }}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            borderRadius: 1.5,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiAlert-icon': {
              color: 'primary.contrastText',
            },
          }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={handleReturnToOwnerDashboard}
              startIcon={<ExitToAppIcon />}
              variant="outlined"
              sx={{ 
                borderColor: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              Return to Owner Dashboard
            </Button>
          }
        >
          <Typography sx={{ fontFamily: lato, fontSize: '0.875rem', fontWeight: 600 }}>
            Viewing {businessName} as administrator
          </Typography>
        </Alert>
      )}

      {isMultiBusinessManager && !isImpersonating && (
        <Alert 
          severity="info"
          sx={{ 
            mb: 2,
            borderRadius: 1.5,
            bgcolor: 'info.main',
            color: 'info.contrastText',
            '& .MuiAlert-icon': {
              color: 'info.contrastText',
            },
          }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={handleReturnToMultiBusinessDashboard}
              startIcon={<ExitToAppIcon />}
              variant="outlined"
              sx={{ 
                borderColor: theme.palette.info.contrastText,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              Return to My Businesses
            </Button>
          }
        >
          <Typography sx={{ fontFamily: lato, fontSize: '0.875rem', fontWeight: 600 }}>
            Managing {businessName}
          </Typography>
        </Alert>
      )}

      {/* Snapshot metrics */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={sectionLabelSx}>Overview</Typography>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.95rem' }}>Dashboard snapshot</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {lastUpdated && (
              <Typography
                sx={{
                  fontFamily: lato,
                  fontSize: '0.75rem',
                  color: 'text.disabled',
                  mr: 0.5,
                }}
              >
                Updated {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
              </Typography>
            )}
            <Tooltip title="Refresh dashboard">
              <span>
                <IconButton
                  size="small"
                  onClick={() => fetchDashboardData(true)}
                  disabled={refreshing}
                  sx={iconActionSx}
                >
                  {refreshing ? <CircularProgress size={18} /> : <RefreshIcon sx={{ fontSize: '1.15rem' }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: 2,
          }}
        >
          {metricCards.map((card) => (
            <Card
              key={card.key}
              onClick={card.onClick}
              sx={{
                cursor: 'pointer',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderLeft: `3px solid ${card.color}`,
                boxShadow: 'none',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  borderLeftColor: card.color,
                },
              }}
            >
              <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: `${card.color}18`,
                      color: card.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...sectionLabelSx, fontSize: '0.6rem', mb: 0.25 }}>
                      {card.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: lato,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        lineHeight: 1.1,
                        color: 'text.primary',
                      }}
                    >
                      {card.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: lato,
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        mt: 0.5,
                        display: { xs: 'none', sm: 'block' },
                      }}
                      noWrap
                    >
                      {card.helper}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
      {/* High priority notifications */}
      {highPriorityNotifications.length > 0 && (
        <DashboardSection
          fullWidth
          label="Alerts"
          title="High priority notifications"
          count={highPriorityNotifications.length}
          icon={<NotificationsActiveIcon />}
          accentColor={theme.palette.error.main}
          action={
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => navigate('/admin/safety-checks')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={sectionLinkButtonSx}
            >
              Safety checks
            </Button>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            {highPriorityNotifications.slice(0, 6).map((notification) => {
                // Extract safety_check_id from action_url if it's a safety check notification
                const extractSafetyCheckId = (actionUrl: string, title: string, message: string): string | null => {
                  if (!actionUrl) {
                    // Try to extract from title or message if action_url is not available
                    // Title format: "Failed Safety Check: {check_name}"
                    // We'll need to query by check name, but for now return null
                    return null;
                  }
                  
                  // Try patterns like /safety-checks/{id}/analysis or /admin/safety-checks/analysis/{id}
                  const patterns = [
                    /\/safety-checks\/([^/?]+)/,
                    /\/admin\/safety-checks\/analysis\/([^/?]+)/,
                    /safety_check_id=([^&]+)/,
                    /safetyCheckId=([^&]+)/,
                    /check_id=([^&]+)/,
                  ];
                  for (const pattern of patterns) {
                    const match = actionUrl.match(pattern);
                    if (match) return match[1];
                  }
                  
                  // If action_url is just a route like /admin/safety-checks?tab=responses
                  // We'll need to fetch recent failed checks and match by title
                  return null;
                };

                const safetyCheckId = notification.type === 'safety_check' 
                  ? extractSafetyCheckId(notification.action_url, notification.title, notification.message) 
                  : null;
                
                // Debug logging
                if (notification.type === 'safety_check') {
                  console.log('🔍 Safety check notification:', {
                    notification_id: notification.notification_id,
                    type: notification.type,
                    title: notification.title,
                    action_url: notification.action_url,
                    extracted_safety_check_id: safetyCheckId,
                    message: notification.message
                  });
                }

                return (
                  <Card key={notification.notification_id} sx={dashboardItemCardSx}>
                    <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {notification.type === 'incident' ? (
                              <ErrorIcon fontSize="small" color="error" />
                            ) : notification.type === 'safety_check' ? (
                              <WarningIcon fontSize="small" color="warning" />
                            ) : null}
                            <Typography sx={{ ...sectionTitleSx, fontSize: '0.8125rem' }}>
                              {notification.title}
                            </Typography>
                        <Chip 
                          size="small" 
                          color={notification.type === 'incident' ? 'error' : 'warning'}
                          label={notification.type === 'incident' ? 'Incident' : 'Safety Check'}
                              sx={{ fontWeight: 600, height: '24px' }}
                            />
                            {notification.severity === 'critical' && (
                              <Chip 
                                size="small" 
                                color="error"
                                label="Critical"
                                sx={{ fontWeight: 600, height: '24px' }}
                              />
                            )}
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.6,
                              mb: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Tooltip title="View Full Message">
                          <IconButton
                            size="small"
                            onClick={() => setNotificationDialog({ open: true, notification })}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark as Read">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                await axios.put(
                                  `${API_BASE_URL}/notifications/platform/${notification.notification_id}/read`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                setPlatformNotifications(prev => 
                                  prev.map(n => 
                                    n.notification_id === notification.notification_id 
                                      ? { ...n, is_read: true } 
                                      : n
                                  )
                                );
                              } catch (err) {
                                console.error('Error marking notification as read:', err);
                              }
                            }}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'success.main',
                                color: 'success.main',
                                '&:hover': { 
                                  bgcolor: 'success.light',
                                  color: 'success.dark'
                              }
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                          {/* View AI Report - round icon under action buttons for safety checks */}
                          {notification.type === 'safety_check' && (
                            <Tooltip title="View AI Report">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  let checkId = safetyCheckId;
                                  
                                  // If we don't have the ID, try to find it by querying failed checks
                                  if (!checkId) {
                                    try {
                                      const token = localStorage.getItem('token');
                                      // Extract check name from title (format: "Failed Safety Check: {check_name}")
                                      const checkNameMatch = notification.title.match(/Failed Safety Check: (.+)/);
                                      
                                      if (checkNameMatch) {
                                        const checkName = checkNameMatch[1].trim();
                                        
                                        // Query check responses endpoint to find failed checks
                                        try {
                                          const responsesRes = await axios.get(
                                            `${API_BASE_URL}/check-responses?check_status=FAIL,fail&limit=20`,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          
                                          if (responsesRes.data?.success && responsesRes.data.data?.length > 0) {
                                            const failedChecks = responsesRes.data.data;
                                            
                                            // Try to find matching check by name
                                            const matchingCheck = failedChecks.find((check: any) => {
                                              const checkNameFromData = check.check_name || '';
                                              return checkNameFromData === checkName || 
                                                     checkNameFromData.includes(checkName) ||
                                                     checkName.includes(checkNameFromData);
                                            });
                                            
                                            if (matchingCheck?.safety_check_id) {
                                              checkId = matchingCheck.safety_check_id;
                                              console.log('Found matching check by name:', checkId);
                                            } else if (failedChecks.length > 0) {
                                              // Use the most recent failed check if we can't match by name
                                              // Sort by created_at descending and take the first one
                                              const sortedChecks = failedChecks.sort((a: any, b: any) => {
                                                const dateA = new Date(a.created_at || 0).getTime();
                                                const dateB = new Date(b.created_at || 0).getTime();
                                                return dateB - dateA;
                                              });
                                              checkId = sortedChecks[0]?.safety_check_id;
                                              console.log('Using most recent failed check:', checkId);
                                            }
                                          }
                                        } catch (err) {
                                          console.error('Error querying check responses:', err);
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Error finding safety check ID:', err);
                                    }
                                  }
                                  
                                  if (!checkId) {
                                    alert('Unable to find the safety check. Please navigate to Safety Checks to view the AI analysis.');
                                    return;
                                  }
                                  
                                  setAiAnalysisDialog({
                                    open: true,
                                    safetyCheckId: checkId,
                                    loading: true,
                                    data: null,
                                    error: null,
                                  });
                                  try {
                                    const token = localStorage.getItem('token');
                                    // For safety checks, use the check-responses/analysis endpoint
                                    const res = await axios.get(
                                      `${API_BASE_URL}/check-responses/analysis/${checkId}`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    if (res.data?.success && res.data.data && res.data.data.length > 0) {
                                      // Get the most recent analysis
                                      const latestAnalysis = res.data.data[0];
                                      // Format it to match the expected structure
                                      const analysisText = latestAnalysis.analysis_text || '';
                                      const isFail = analysisText.trim().split('\n')[0] === 'FAIL';
                                      
                                      setAiAnalysisDialog(prev => ({
                                        ...prev,
                                        loading: false,
                                        data: {
                                          analysis_text: analysisText,
                                          detailed_explanation: analysisText,
                                          should_mark_out_of_action: isFail,
                                          created_at: latestAnalysis.created_at,
                                          analysis_id: latestAnalysis.analysis_id
                                        },
                                        error: null,
                                      }));
                                    } else {
                                      setAiAnalysisDialog(prev => ({
                                        ...prev,
                                        loading: false,
                                        error: 'No AI analysis found for this safety check',
                                      }));
                                    }
                                  } catch (e: any) {
                                    setAiAnalysisDialog(prev => ({
                                      ...prev,
                                      loading: false,
                                      error: e.response?.data?.message || 'Failed to fetch AI analysis',
                                    }));
                                  }
                                }}
                                sx={{ 
                                  mt: 0.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  '&:hover': { 
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.main',
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                <AssessmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                        </Box>
                    </CardContent>
                  </Card>
                );
              })}
          </Box>
        </DashboardSection>
      )}

      {outOfActionAssets.length > 0 && (
        <DashboardSection
          label="Assets"
          title="Out of action"
          count={outOfActionAssets.length}
          icon={<BlockIcon />}
          accentColor={theme.palette.warning.main}
          action={
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => navigate('/admin/assets')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={sectionLinkButtonSx}
            >
              View assets
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {outOfActionAssets.slice(0, 8).map((asset) => (
              <Card key={asset.asset_id} sx={dashboardItemCardSx}>
                <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BlockIcon fontSize="small" color="warning" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                          {asset.asset_name || 'Unnamed asset'}
                        </Typography>
                        <Chip
                          size="small"
                          color="warning"
                          label="Out of Action"
                          sx={{ fontWeight: 600, height: '24px' }}
                        />
                      </Box>
                      {asset.out_of_action_note && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {asset.out_of_action_note}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {asset.out_of_action_date
                          ? `Since ${formatDistanceToNow(new Date(asset.out_of_action_date), { addSuffix: true })}`
                          : 'No date recorded'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="View in Assets">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/admin/assets')}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View AI Report">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            setOutOfActionReportDialog({
                              open: true,
                              asset,
                              note: null,
                              loading: true,
                              error: null,
                            });
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.get(`${API_BASE_URL}/assets/out-of-action-audit`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const auditList = res.data?.data || [];
                              const markedOut = auditList
                                .filter(
                                  (e: any) =>
                                    e.asset_id === asset.asset_id && e.action === 'marked_out_of_action'
                                )
                                .sort(
                                  (a: any, b: any) =>
                                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                                );
                              const noteFromAudit = markedOut[0]?.note ?? null;
                              const note = noteFromAudit ?? asset.out_of_action_note ?? null;
                              setOutOfActionReportDialog((prev) => ({
                                ...prev,
                                note,
                                loading: false,
                                error: note ? null : 'No report or note found for this asset.',
                              }));
                            } catch (err: any) {
                              setOutOfActionReportDialog((prev) => ({
                                ...prev,
                                note: asset.out_of_action_note ?? null,
                                loading: false,
                                error: err.response?.data?.message || 'Failed to load report.',
                              }));
                            }
                          }}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main', color: 'primary.main' },
                          }}
                        >
                          <AssessmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DashboardSection>
      )}

      {pendingIncidents.length > 0 && (
        <DashboardSection
          label="Compliance"
          title="Incidents requiring attention"
          count={pendingIncidents.length}
          icon={<ReportProblemIcon />}
          accentColor={theme.palette.info.main}
          action={
            <Button
              size="small"
              variant="outlined"
              color="info"
              onClick={() => navigate('/admin/incidents')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={sectionLinkButtonSx}
            >
              View incidents
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {pendingIncidents.slice(0, 8).map((incident) => (
              <Card key={incident.incident_id} sx={dashboardItemCardSx}>
                <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <ReportProblemIcon fontSize="small" color="info" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                          {incident.title || 'Untitled incident'}
                        </Typography>
                        <Chip
                          size="small"
                          color={incident.status === 'action_required' ? 'warning' : 'info'}
                          label={incident.status === 'action_required' ? 'Action Required' : 'Reported'}
                          sx={{ fontWeight: 600, height: '24px' }}
                        />
                        {incident.severity && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={String(incident.severity).replace(/^./, (c: string) => c.toUpperCase())}
                            sx={{ height: '24px' }}
                          />
                        )}
                      </Box>
                      {incident.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {incident.description}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {formatDistanceToNow(new Date(incident.created_at), { addSuffix: true })}
                      </Typography>
                    </Box>
                    <Tooltip title="View in Incidents">
                      <IconButton
                        size="small"
                        onClick={() => navigate('/admin/incidents')}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DashboardSection>
      )}

      {dvlaHighUnreadNotifications.length > 0 && (
        <DashboardSection
          label="Compliance"
          title="DVLA notifications"
          count={dvlaHighUnreadNotifications.length}
          icon={<ListAltIcon />}
          accentColor={theme.palette.primary.main}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/admin/dvla')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={sectionLinkButtonSx}
            >
              View DVLA
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {dvlaHighUnreadNotifications.slice(0, 8).map((notification) => (
              <Card key={notification.id} sx={dashboardItemCardSx}>
                <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <ListAltIcon fontSize="small" color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                          {notification.type ? String(notification.type).replace(/_/g, ' ') : 'DVLA'}
                        </Typography>
                        <Chip size="small" color="primary" label="High" sx={{ fontWeight: 600, height: '24px' }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          lineHeight: 1.6,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {notification.message || 'No message'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="View in DVLA">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/admin/dvla')}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Read">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              const t = localStorage.getItem('token');
                              await axios.put(
                                `${API_BASE_URL}/dvla-staff/notifications/${notification.id}/read`,
                                {},
                                { headers: { Authorization: `Bearer ${t}` } }
                              );
                              setDvlaHighUnreadNotifications((prev) =>
                                prev.filter((n) => n.id !== notification.id)
                              );
                            } catch (err) {
                              console.error('Error marking DVLA notification as read:', err);
                            }
                          }}
                          sx={{
                            border: '1px solid',
                            borderColor: 'success.main',
                            color: 'success.main',
                            '&:hover': {
                              bgcolor: 'success.light',
                              color: 'success.dark',
                            },
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DashboardSection>
      )}

      {inTransitOutgoingDeliveries.length > 0 && (
        <DashboardSection
          label="Operations"
          title="Deliveries in transit"
          count={inTransitOutgoingDeliveries.length}
          icon={<LocalShippingIcon />}
          accentColor={theme.palette.success.main}
          action={
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => navigate('/admin/delivery-checks')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={sectionLinkButtonSx}
            >
              Delivery checks
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {inTransitOutgoingDeliveries.slice(0, 8).map((delivery) => (
              <Card key={delivery.request_id} sx={dashboardItemCardSx}>
                <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <LocalShippingIcon fontSize="small" color="success" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                          {delivery.asset_name || delivery.temporary_asset_specification || ('WGS' + (delivery.whole_good_number_prefix && delivery.whole_good_number_prefix !== 'WGS' ? delivery.whole_good_number_prefix : '') + (delivery.whole_good_number || '—'))}
                        </Typography>
                        <Chip
                          size="small"
                          color="success"
                          label="In Transit"
                          sx={{ fontWeight: 600, height: '24px' }}
                        />
                      </Box>
                      {delivery.check_name && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          {delivery.check_name}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {delivery.whole_good_number && `WGS${delivery.whole_good_number_prefix && delivery.whole_good_number_prefix !== 'WGS' ? delivery.whole_good_number_prefix : ''}${delivery.whole_good_number} · `}
                        {formatDistanceToNow(new Date(delivery.created_at || delivery.received_at), { addSuffix: true })}
                      </Typography>
                    </Box>
                    <Tooltip title="View in Delivery Checks">
                      <IconButton
                        size="small"
                        onClick={() => navigate('/admin/delivery-checks')}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DashboardSection>
      )}

      </Box>

      {/* Out of Action AI Report / Note Dialog */}
      <Dialog
        open={outOfActionReportDialog.open}
        onClose={() =>
          setOutOfActionReportDialog({ open: false, asset: null, note: null, loading: false, error: null })
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="action" />
          AI Report
          {outOfActionReportDialog.asset && (
            <Typography component="span" variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
              — {outOfActionReportDialog.asset.asset_name || 'Unnamed asset'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {outOfActionReportDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {!outOfActionReportDialog.loading && outOfActionReportDialog.error && (
            <Alert severity="warning">{outOfActionReportDialog.error}</Alert>
          )}
          {!outOfActionReportDialog.loading && outOfActionReportDialog.note && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                maxHeight: 360,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'inherit', m: 0 }}>
                {outOfActionReportDialog.note}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setOutOfActionReportDialog({ open: false, asset: null, note: null, loading: false, error: null })
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Full Message Dialog */}
      <Dialog
        open={notificationDialog.open}
        onClose={() => {
          setNotificationDialog({ open: false, notification: null });
          setSafetyCheckAssessment({ loading: false, data: null, error: null, responses: [] });
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {notificationDialog.notification && (
          <>
              <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      size="small" 
                      color={notificationDialog.notification.type === 'incident' ? 'error' : 'warning'}
                      label={notificationDialog.notification.type === 'incident' ? 'Incident' : 'Safety Check'}
                        sx={{ fontWeight: 600 }}
                    />
                    {notificationDialog.notification.severity && (
                      <Chip 
                        size="small" 
                        label={notificationDialog.notification.severity}
                        color={
                          notificationDialog.notification.severity === 'critical' ? 'error' : 
                          notificationDialog.notification.severity === 'high' ? 'warning' : 
                          'default'
                        }
                          sx={{ fontWeight: 600 }}
                      />
                    )}
                    {!notificationDialog.notification.is_read && (
                      <Chip 
                        size="small" 
                        label="Unread" 
                        color="primary"
                          sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {notificationDialog.notification.title}
                  </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {new Date(notificationDialog.notification.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="close"
                    onClick={() => {
                      setNotificationDialog({ open: false, notification: null });
                      setSafetyCheckAssessment({ loading: false, data: null, error: null, responses: [] });
                    }}
                  sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent 
              dividers
              sx={{
                flex: '1 1 auto',
                overflowY: 'auto',
                minHeight: 0,
                  py: 3
              }}
            >
                {/* Notification Message */}
                <Box sx={{ mb: notificationDialog.notification.type === 'safety_check' ? 3 : 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                    Notification Message
                  </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                      lineHeight: 1.8,
                  color: 'text.primary',
                }}
              >
                {notificationDialog.notification.message}
              </Typography>
                </Box>

                {/* Safety Check Assessment */}
                {notificationDialog.notification.type === 'safety_check' && (() => {
                  const extractSafetyCheckId = (actionUrl: string): string | null => {
                    if (!actionUrl) return null;
                    const patterns = [
                      /\/safety-checks\/([^/?]+)/,
                      /\/admin\/safety-checks\/analysis\/([^/?]+)/,
                      /safety_check_id=([^&]+)/,
                    ];
                    for (const pattern of patterns) {
                      const match = actionUrl.match(pattern);
                      if (match) return match[1];
                    }
                    return null;
                  };
                  const safetyCheckId = extractSafetyCheckId(notificationDialog.notification.action_url);
                  return safetyCheckId ? (
                  <Box sx={{ mt: 3, pt: 3, borderTop: 2, borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                      Safety Check Assessment
                    </Typography>
                    
                    {safetyCheckAssessment.loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                      </Box>
                    ) : safetyCheckAssessment.error ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {safetyCheckAssessment.error}
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* AI Failure Report */}
                        {safetyCheckAssessment.data && (
                          <Box sx={{ 
                            p: 3, 
                            bgcolor: '#fff5f5', 
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'error.main',
                            boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                              <WarningIcon color="error" sx={{ fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.dark' }}>
                                AI Failure Report
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                              <Chip
                                label="FAIL"
                                color="error"
                                sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                              />
                              {safetyCheckAssessment.data.asset_marked_out_of_action && (
                                <Chip
                                  label="Asset Marked Out of Action"
                                  color="warning"
                                  sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                                />
                              )}
                            </Box>
                            
                            {safetyCheckAssessment.data.detailed_explanation && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                                  Failure Analysis:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.8,
                                    color: 'text.primary',
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}
                                >
                                  {safetyCheckAssessment.data.detailed_explanation}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Report Generated: {new Date(safetyCheckAssessment.data.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {!safetyCheckAssessment.data && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            AI analysis report is not available for this safety check. The failure may have been determined by other means.
                          </Alert>
                        )}

                        {/* Responses */}
                        {safetyCheckAssessment.responses.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>
                              Check Responses ({safetyCheckAssessment.responses.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {safetyCheckAssessment.responses.map((response: any, idx: number) => (
                                <Paper key={idx} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {response.question_text || 'Question'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {response.response_text || 'No response'}
                                  </Typography>
                                  {response.user_name && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      By: {response.user_name} • {new Date(response.created_at).toLocaleString()}
                                    </Typography>
                                  )}
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {!safetyCheckAssessment.data && safetyCheckAssessment.responses.length === 0 && (
                          <Alert severity="info">
                            No assessment data available for this safety check.
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                  ) : null;
                })()}
            </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', gap: 1 }}>
              {notificationDialog.notification.type === 'safety_check' && (() => {
                const extractSafetyCheckId = (actionUrl: string): string | null => {
                  if (!actionUrl) return null;
                  const patterns = [
                    /\/safety-checks\/([^/?]+)/,
                    /\/admin\/safety-checks\/analysis\/([^/?]+)/,
                    /safety_check_id=([^&]+)/,
                  ];
                  for (const pattern of patterns) {
                    const match = actionUrl.match(pattern);
                    if (match) return match[1];
                  }
                  return null;
                };
                const safetyCheckId = extractSafetyCheckId(notificationDialog.notification.action_url);
                
                return safetyCheckId ? (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<AssessmentIcon />}
                    onClick={async () => {
                      setAiAnalysisDialog({
                        open: true,
                        safetyCheckId,
                        loading: true,
                        data: null,
                        error: null,
                      });
                      try {
                        const token = localStorage.getItem('token');
                        // For safety checks, use the check-responses/analysis endpoint
                        const res = await axios.get(
                          `${API_BASE_URL}/check-responses/analysis/${safetyCheckId}`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (res.data?.success && res.data.data && res.data.data.length > 0) {
                          // Get the most recent analysis
                          const latestAnalysis = res.data.data[0];
                          // Format it to match the expected structure
                          const analysisText = latestAnalysis.analysis_text || '';
                          const isFail = analysisText.trim().split('\n')[0] === 'FAIL';
                          
                          setAiAnalysisDialog(prev => ({
                            ...prev,
                            loading: false,
                            data: {
                              analysis_text: analysisText,
                              detailed_explanation: analysisText,
                              should_mark_out_of_action: isFail,
                              created_at: latestAnalysis.created_at,
                              analysis_id: latestAnalysis.analysis_id
                            },
                            error: null,
                          }));
                        } else {
                          setAiAnalysisDialog(prev => ({
                            ...prev,
                            loading: false,
                            error: 'No AI analysis found for this safety check',
                          }));
                        }
                      } catch (e: any) {
                        setAiAnalysisDialog(prev => ({
                          ...prev,
                          loading: false,
                          error: e.response?.data?.message || 'Failed to fetch AI analysis',
                        }));
                      }
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    View AI Failure Report
                  </Button>
                ) : null;
              })()}
              {!notificationDialog.notification.is_read && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(
                        `${API_BASE_URL}/notifications/platform/${notificationDialog.notification!.notification_id}/read`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      setPlatformNotifications(prev => 
                        prev.map(n => 
                          n.notification_id === notificationDialog.notification!.notification_id 
                            ? { ...n, is_read: true } 
                            : n
                        )
                      );
                      
                      setNotificationDialog(prev => ({
                        ...prev,
                        notification: prev.notification ? { ...prev.notification, is_read: true } : null
                      }));
                    } catch (err) {
                      console.error('Error marking notification as read:', err);
                    }
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Mark as Read
                </Button>
              )}
              <Button
                  onClick={() => {
                    setNotificationDialog({ open: false, notification: null });
                    setSafetyCheckAssessment({ loading: false, data: null, error: null, responses: [] });
                  }}
                variant="outlined"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog
        open={aiAnalysisDialog.open}
        onClose={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              AI Analysis
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            minHeight: 0,
            py: 3
          }}
        >
          {aiAnalysisDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : aiAnalysisDialog.error ? (
            <Alert severity="error">{aiAnalysisDialog.error}</Alert>
          ) : aiAnalysisDialog.data ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Status Summary */}
              <Box sx={{ 
                p: 2.5, 
                bgcolor: aiAnalysisDialog.data.should_mark_out_of_action ? '#fff5f5' : '#f1f8f4', 
                borderRadius: 2,
                border: '2px solid',
                borderColor: aiAnalysisDialog.data.should_mark_out_of_action ? 'error.main' : 'success.main'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={aiAnalysisDialog.data.should_mark_out_of_action ? 'FAIL' : 'PASS'}
                    color={aiAnalysisDialog.data.should_mark_out_of_action ? 'error' : 'success'}
                    sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                  />
                  {aiAnalysisDialog.data.asset_marked_out_of_action && (
                    <Chip
                      label="Asset Marked Out of Action"
                      color="warning"
                      sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Analysis Date: {new Date(aiAnalysisDialog.data.created_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Detailed Explanation */}
              {(aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text) && (
                <Box sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                    Detailed Analysis
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.8,
                      color: 'text.primary'
                    }}
                  >
                    {aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text}
                  </Typography>
                </Box>
              )}

              {!aiAnalysisDialog.data.detailed_explanation && !aiAnalysisDialog.data.analysis_text && (
                <Alert severity="info">
                  No detailed analysis available for this safety check.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdminDashboard; 